import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import './Dashboard.css';

interface User {
  customer_id: string;
  email: string;
  company_name?: string;
  country?: string;
  credits: number;
  require_password_change?: boolean;
}

interface CreditSummary {
  customer_id: string;
  current_credits: number;
  analyses_available: number;
  total_purchased: number;
  total_consumed: number;
  recent_transactions: any[];
  pricing: {
    credits_per_package: number;
    package_price_usd: number;
    credits_per_analysis: number;
    price_per_analysis: number;
    /** Online (Stripe) purchases; switched off on the admin System page */
    purchases_enabled?: boolean;
  };
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [creditSummary, setCreditSummary] = useState<CreditSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [user, setUser] = useState<User | null>(null);

  const loadCreditSummary = useCallback(async (customerIdParam?: string) => {
    const targetCustomerId = customerIdParam || user?.customer_id;
    if (!targetCustomerId) return;

    setLoading(true);
    setError(null);
    try {
      const summary = await apiService.getCreditBalance(targetCustomerId);
      setCreditSummary(summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credit information');
    } finally {
      setLoading(false);
    }
  }, [user?.customer_id]);

  useEffect(() => {
    // Get authenticated user data
    const userData = localStorage.getItem('user_data');
    if (!userData) {
      // Not authenticated, redirect to login
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      loadCreditSummary(parsedUser.customer_id);

      // Handle payment success/failure from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get('payment');
      if (paymentStatus === 'success') {
        setError(null);
        // Show success message and refresh credits
        setTimeout(() => {
          loadCreditSummary(parsedUser.customer_id);
          // Clear URL parameters after processing
          window.history.replaceState({}, '', '/dashboard');
        }, 1000);
      } else if (paymentStatus === 'cancel') {
        setError('Payment was cancelled. You can try again anytime.');
        // Clear URL parameters after processing
        setTimeout(() => {
          window.history.replaceState({}, '', '/dashboard');
        }, 3000);
      }
    } catch (error) {
      console.error('Error parsing user data:', error);
      navigate('/login');
      return;
    }
  }, [navigate, loadCreditSummary]); // Include loadCreditSummary dependency

  const handlePurchaseCredits = async () => {
    if (!user?.customer_id) return;

    setPurchasing(true);
    try {
      // Create Stripe checkout session
      const formData = new FormData();
      formData.append('customer_id', user.customer_id);
      formData.append('success_url', `${window.location.origin}/dashboard?payment=success`);
      formData.append('cancel_url', `${window.location.origin}/dashboard?payment=cancel`);
      
      const token = localStorage.getItem('access_token');
      const response = await fetch('/api/payments/create-checkout', {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: formData
      });
      
      const data = await response.json();
      
      if (data.status === 'success' && data.data.checkout_url) {
        // Redirect to Stripe checkout
        window.location.href = data.data.checkout_url;
      } else {
        throw new Error(data.message || 'Failed to create checkout session');
      }
      
    } catch (error) {
      console.error('Stripe checkout error:', error);
      alert('Payment system error: ' + (error instanceof Error ? error.message : 'Please try again'));
    } finally {
      setPurchasing(false);
    }
  };


  return (
    <div className="dashboard-page">
      <h1>Proven Credit Dashboard</h1>
      <p className="page-description">
        Manage your EUDR compliance analysis credits and view transaction history.
      </p>

      {/* Welcome message for authenticated user */}
      {user && (
        <div className="user-welcome">
          <p>Welcome back, <strong>{user.email}</strong></p>
          {user.company_name && <p>Company: {user.company_name}</p>}
        </div>
      )}

      {/* Payment Success/Error Messages */}
      {new URLSearchParams(window.location.search).get('payment') === 'success' && (
        <div className="success-message">
          ✓ Payment successful! Your credits are being added to your account.
          <div className="processing-indicator">
            <div className="spinner"></div>
            <span>Processing payment...</span>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          ✗ {error}
          {!error.includes('cancelled') && (
            <button onClick={() => loadCreditSummary()} className="retry-btn">
              Retry
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <span>Loading credit information...</span>
        </div>
      )}

      {creditSummary && (
        <div className="dashboard-content">
          {/* Credit Summary Cards */}
          <div className="credit-cards">
            <div className="credit-card">
              <div className="card-header">
                <span className="card-icon">💳</span>
                <span className="card-title">Current Credits</span>
              </div>
              <div className="card-value">{creditSummary.current_credits}</div>
              <div className="card-subtitle">Available for analysis</div>
            </div>

            <div className="credit-card">
              <div className="card-header">
                <span className="card-icon">📊</span>
                <span className="card-title">Analyses Available</span>
              </div>
              <div className="card-value">{creditSummary.analyses_available}</div>
              <div className="card-subtitle">At 50 credits each</div>
            </div>

            <div className="credit-card">
              <div className="card-header">
                <span className="card-icon">🛒</span>
                <span className="card-title">Total Purchased</span>
              </div>
              <div className="card-value">{creditSummary.total_purchased}</div>
              <div className="card-subtitle">Credits bought</div>
            </div>

            <div className="credit-card">
              <div className="card-header">
                <span className="card-icon">📈</span>
                <span className="card-title">Total Used</span>
              </div>
              <div className="card-value">{creditSummary.total_consumed}</div>
              <div className="card-subtitle">Credits consumed</div>
            </div>
          </div>

          {/* Purchase Section */}
          <div className="purchase-section">
            <h3>Purchase Credits</h3>
            <div className="pricing-info">
              <div className="pricing-card">
                <div className="pricing-header">
                  <h4>Credit Package</h4>
                  <div className="price">${creditSummary.pricing.package_price_usd}</div>
                </div>
                <div className="pricing-features">
                  <div className="feature">✓ {creditSummary.pricing.credits_per_package} Credits</div>
                  <div className="feature">✓ {creditSummary.pricing.credits_per_package / creditSummary.pricing.credits_per_analysis} EUDR Analyses</div>
                  <div className="feature">✓ ${creditSummary.pricing.price_per_analysis} per analysis</div>
                  <div className="feature">✓ Instant activation</div>
                </div>
                {creditSummary.pricing.purchases_enabled === false ? (
                  <p
                    className="purchase-unavailable"
                    role="note"
                    style={{ background: '#fff3cd', border: '1px solid #ffecb5', borderRadius: 8, padding: '1rem', color: '#856404', margin: '1rem 0 0' }}
                  >
                    Online purchases are temporarily unavailable. To top up your credits, email{' '}
                    <a href="mailto:info@use-proven.com?subject=Credit%20top-up">info@use-proven.com</a>{' '}
                    and we will add them to your account.
                  </p>
                ) : (
                  <button
                    onClick={handlePurchaseCredits}
                    disabled={purchasing}
                    className="btn primary purchase-btn"
                  >
                    {purchasing ? 'Processing...' : `Purchase ${creditSummary.pricing.credits_per_package} Credits`}
                  </button>
                )}
              </div>
            </div>

            {creditSummary.current_credits < creditSummary.pricing.credits_per_analysis && (
              <div className="low-credits-warning">
                <h4><strong>⚠</strong> Low Credits Warning</h4>
                <p>You need at least {creditSummary.pricing.credits_per_analysis} credits to run an analysis. Consider purchasing more credits to continue using the service.</p>
              </div>
            )}
          </div>

          {/* Transaction History */}
          <div className="transaction-section">
            <h3>Recent Transactions</h3>
            {creditSummary.recent_transactions.length > 0 ? (
              <div className="transaction-table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Credits</th>
                      <th>Balance</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditSummary.recent_transactions.map((transaction, index) => (
                      <tr key={transaction.id || index}>
                        <td>
                          {new Date(transaction.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </td>
                        <td>
                          <span className={`transaction-type ${transaction.transaction_type}`}>
                            {transaction.transaction_type === 'purchase' ? '🛒 Purchase' : '📊 Analysis'}
                          </span>
                        </td>
                        <td className={transaction.credits_changed > 0 ? 'credits-positive' : 'credits-negative'}>
                          {transaction.credits_changed > 0 ? '+' : ''}{transaction.credits_changed}
                        </td>
                        <td>{transaction.remaining_credits}</td>
                        <td>{transaction.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="no-transactions">
                <p>No transactions yet. Purchase credits to get started!</p>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="dashboard-actions">
            <button 
              onClick={() => navigate('/upload')}
              className="btn primary"
            >
              Upload Documents
            </button>
            <button 
              onClick={() => navigate('/pricing')}
              className="btn secondary"
            >
              View Pricing
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;